/* ============================================================
   BBW · Cart Drawer controller
   Talks to Shopify's AJAX Cart (/cart/add.js, /cart/change.js)
   and refreshes the drawer via the Section Rendering API.

   Sync model (fixes raciness, slow removes, image flashing):
   - SEQUENCING  every request takes a token; only the latest
     response may touch the DOM, so a stale render can never
     resurrect a removed item.
   - MORPHING    server HTML is reconciled per line-item key —
     existing item nodes (and their <img>s) are reused, only
     changed text is patched → no flash on add/resync.
   - OPTIMISTIC  qty/remove update the row instantly; requests
     are serialized per line and rapid clicks collapse into a
     single request carrying the final quantity.
   - RESYNC      after any change, re-fetch at 650/1400/2600 ms
     so server-side gift/discount apps settle; a resync that
     brings identical content is a no-op (morph diffs nothing).
   ============================================================ */
(function () {
  var SECTION = 'bbw-cart-drawer';

  var seq = 0;              // response-ordering token
  var resyncTimers = [];
  var lines = {};           // key -> { desired, inFlight, sendTimer }
  var byobSessionClearPromise = null;
  var byobCheckoutPromise = null;
  var checkoutButtonStates = new Map();
  var checkoutButtonLabels = new WeakMap();
  var checkoutNeedsRecheck = false;
  var checkoutRecoveryMessage = '';
  var checkoutControlsObserver = null;
  var checkoutBusyMessage = 'Preparing checkout…';
  var cartEditVersion = 0;
  var pendingCartAdds = 0;
  var directCheckoutForms = new WeakMap();

  function root() { return document.querySelector('[data-bbw-cart]'); }
  function contentEl() { var r = root(); return r ? r.querySelector('[data-cart-content]') : null; }

  function open() {
    var r = root();
    if (!r) return;
    r.classList.add('is-open');
    r.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bbw-cart-lock');
    applyUpsell();
    syncRailBars();
    syncWallets();
  }
  function close() {
    var r = root();
    if (!r) return;
    r.classList.remove('is-open');
    r.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bbw-cart-lock');
  }

  function updateCount(n) {
    if (n === undefined || n === null) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
    });
  }

  function isHiddenCartLine(item) {
    var props = item && item.properties ? item.properties : {};
    return props._instant_hidden === 'true' || props['_instant-hidden'] === 'true';
  }

  function isByobCartLine(item) {
    var props = item && item.properties ? item.properties : {};
    return props._byob_item === 'true' || props._byob_slot != null || props._byob_gift === 'true';
  }

  function isPaidByobCartLine(item) {
    return !isHiddenCartLine(item) && isByobCartLine(item) &&
      (!item.properties || item.properties._byob_gift !== 'true');
  }

  function hasPaidByobItems(cart) {
    return ((cart && cart.items) || []).some(isPaidByobCartLine);
  }

  function isLegacyEasyGiftBag(item) {
    return item && item.handle === 'toiletry-bag' && item.properties &&
      Object.prototype.hasOwnProperty.call(item.properties, '_Gifted');
  }

  function byobGiftLineUpdates(cart) {
    var updates = {};
    ((cart && cart.items) || []).forEach(function (item) {
      var props = item && item.properties ? item.properties : {};
      if (props._byob_gift === 'true' && item.key) updates[item.key] = 0;
    });
    return updates;
  }

  function cartNeedsByobSessionClear(cart, exclusiveOffer) {
    // Normal shopping keeps a marked set even when other products are added.
    // Exclusive offer pages deliberately replace the set through their own flow.
    if (!exclusiveOffer && hasPaidByobItems(cart)) return false;
    var attrs = cart && cart.attributes ? cart.attributes : {};
    var hasByobGift = ((cart && cart.items) || []).some(function (item) {
      return item && item.properties && item.properties._byob_gift === 'true';
    });
    if (String(attrs.byob_session || '') !== 'true' && !hasByobGift) return false;
    return ((cart && cart.items) || []).some(function (item) {
      return !isHiddenCartLine(item) && !isByobCartLine(item) &&
        !(hasPaidByobItems(cart) && isLegacyEasyGiftBag(item));
    });
  }

  function clearByobSessionIfMixedCart(cart, exclusiveOffer, check) {
    // Normal shopping must never silently erase a paid set's offer. Exclusive
    // offer pages still intentionally replace it through their explicit flow.
    if (!exclusiveOffer && hasPaidByobItems(cart)) return Promise.resolve(cart);
    if (!cartNeedsByobSessionClear(cart, exclusiveOffer)) return Promise.resolve(cart);
    if (byobSessionClearPromise) return checkoutWithin(byobSessionClearPromise, check);
    var updates = byobGiftLineUpdates(cart);
    var body = {
      attributes: {
        byob_session: '',
        byob_initiated_at: ''
      },
      sections: SECTION,
      sections_url: window.location.pathname
    };
    if (Object.keys(updates).length) body.updates = updates;

    byobSessionClearPromise = cartRequest('/cart/update.js', body, check)
      .then(function (updatedCart) {
        if (updatedCart && updatedCart.sections) applySections(updatedCart.sections);
        return updatedCart;
      })
      .catch(function (err) {
        console.warn('[bbw-cart] BYOB session clear failed', err);
        if (check) throw err;
        return cart;
      })
      .finally(function () {
        byobSessionClearPromise = null;
      });

    return byobSessionClearPromise;
  }

  function surplusEasyGiftBagUpdates(cart) {
    var eligibleQuantity = 0;
    var giftLines = [];
    var giftQuantity = 0;

    ((cart && cart.items) || []).forEach(function (item) {
      var quantity = Number(item.quantity) || 0;
      var properties = item.properties || {};
      var isGift = item.handle === 'toiletry-bag' &&
        Object.prototype.hasOwnProperty.call(properties, '_Gifted');
      if (isGift) {
        giftLines.push(item);
        giftQuantity += quantity;
      } else if (item.handle !== 'toiletry-bag') {
        eligibleQuantity += quantity;
      }
    });

    var excess = giftQuantity - Math.floor(eligibleQuantity / 4);
    var updates = {};
    for (var i = giftLines.length - 1; i >= 0 && excess > 0; i--) {
      var line = giftLines[i];
      if (!line.key) continue;
      var remove = Math.min(Number(line.quantity) || 0, excess);
      updates[line.key] = (Number(line.quantity) || 0) - remove;
      excess -= remove;
    }
    return updates;
  }

  function removeSurplusEasyGiftBags(cart, requestSeq) {
    var updates = surplusEasyGiftBagUpdates(cart);
    if (!Object.keys(updates).length) return Promise.resolve(cart);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: updates,
        sections: SECTION,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('EasyGift bag correction failed: ' + r.status);
        return r.json();
      })
      .then(function (updatedCart) {
        if (requestSeq === seq && !hasPendingOps() && updatedCart && updatedCart.sections) {
          applySections(updatedCart.sections);
        }
        return updatedCart;
      })
      .catch(function (err) {
        console.warn('[bbw-cart] EasyGift bag correction failed', err);
        return cart;
      });
  }

  function checkoutDestinationForCart(cart, fallback, recoveryCodes, removedCodes) {
    var isCheckoutPath = /^\/(?:[^/?#]+\/)*checkout$/.test(fallback);
    var checkoutPath = isCheckoutPath ? fallback : '/checkout';
    if (!hasPaidByobItems(cart)) {
      if (!isCheckoutPath) return fallback;
      var ordinaryParams = new URLSearchParams();
      ordinaryParams.append('discount', checkoutCodes(cart, '', removedCodes).filter(function (code) {
        return !/^(BUILDYOURSET|BYOBFREEGIFTS)$/i.test(code);
      }).join(','));
      return checkoutPath + '?' + ordinaryParams.toString();
    }
    if (!cart.attributes || String(cart.attributes.byob_session || '') !== 'true') {
      throw new Error('BYOB session is unavailable');
    }

    var cartRoot = root();
    var codes = cartRoot ? (cartRoot.getAttribute('data-byob-discount-codes') || '').trim() : '';
    if (!codes) throw new Error('BYOB checkout codes are unavailable');
    var params = new URLSearchParams();
    params.append('discount', checkoutCodes(cart, codes + ',' + (recoveryCodes || []).join(','), removedCodes).join(','));
    return checkoutPath + '?' + params.toString();
  }

  function checkoutCodes(cart, configured, removedCodes) {
    var codes = [];
    var seen = {};
    ((cart && cart.discount_codes) || []).map(function (discount) { return discount.code; })
      .concat((configured || '').split(',')).forEach(function (code) {
        code = String(code || '').trim();
        if (code && !seen[code.toUpperCase()] && !(removedCodes || []).some(function (removed) { return removed.toUpperCase() === code.toUpperCase(); })) {
          seen[code.toUpperCase()] = true;
          codes.push(code);
        }
      });
    return codes;
  }

  function checkoutFailure(reason, message) {
    var error = new Error(message);
    error.checkoutReason = reason;
    return error;
  }

  function checkoutWithin(promise, check, controller) {
    if (!check) return promise;
    var timer;
    return Promise.race([promise, new Promise(function (_, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(checkoutFailure('timeout', 'Checkout verification timed out'));
      }, Math.max(0, check.deadline - Date.now()));
    })]).finally(function () { clearTimeout(timer); });
  }

  // Only checkout repairs use this record. Elapsed time is not proof that a
  // write failed. Keep it across reloads/tabs until a response or tagged read
  // proves completion; never put its contents in checkout diagnostics.
  var CHECKOUT_WRITE_KEY = 'bbw:checkout-write-v1';

  function cartFingerprint(cart) {
    var token = String(cart && cart.token || '');
    if (!token) throw checkoutFailure('recovery_unavailable', 'Cart identity is unavailable');
    var hash = 2166136261;
    for (var i = 0; i < token.length; i++) hash = Math.imul(hash ^ token.charCodeAt(i), 16777619);
    return token.length + ':' + (hash >>> 0).toString(16);
  }

  function pendingWrite() {
    var raw = window.localStorage.getItem(CHECKOUT_WRITE_KEY);
    if (!raw) return null;
    var record = JSON.parse(raw);
    if (!record || typeof record.id !== 'string' || typeof record.cart !== 'string' || !Array.isArray(record.items) || !Array.isArray(record.codes)) {
      throw checkoutFailure('recovery_unavailable', 'Invalid pending cart update');
    }
    return record;
  }

  function finishWrite(id) {
    try { if ((pendingWrite() || {}).id === id) window.localStorage.removeItem(CHECKOUT_WRITE_KEY); } catch (_) {}
  }

  function settleWrite(id) {
    // Keep coupon intent until verified handoff, including a failed/hung body.
    // A late response must never alter another operation's protection.
    try {
      var record = pendingWrite();
      if (record && record.id === id) {
        record.settled = true;
        window.localStorage.setItem(CHECKOUT_WRITE_KEY, JSON.stringify(record));
      }
    } catch (_) {}
  }

  function checkPendingWrite(cart, check) {
    check.cartFingerprint = cartFingerprint(cart);
    var record;
    try { record = pendingWrite(); } catch (_) {
      check.recoveryStorageUnavailable = true;
      return;
    }
    if (!record || record.cart !== check.cartFingerprint) { checkoutNeedsRecheck = false; return; }
    var provenByRead = record.items.length ? record.items.every(function (expected) {
      return cart.items.some(function (item) {
        return Number(item.variant_id || item.id) === expected.id && item.quantity >= expected.quantity &&
          item.properties && item.properties._byob_repair === record.id;
      });
    }) : cart.attributes && cart.attributes.byob_repair_write === record.id;
    // A response may arrive while this read is in flight. Its old snapshot is
    // not safe to repair: require tagged proof or a read begun after settlement.
    var settledBeforeRead = record.settled === true && check.readWrite &&
      check.readWrite.id === record.id && check.readWrite.settled === true;
    if (!provenByRead && !settledBeforeRead) {
      check.uncertainWrite = true;
      throw checkoutFailure('write_unknown', 'The earlier cart update is still unconfirmed');
    }
    check.recoveryCodes = record.codes;
    check.removedCodes = record.removedCodes || [];
    check.lastWriteId = record.id;
    if (provenByRead && record.settled !== true) settleWrite(record.id);
    checkoutNeedsRecheck = false;
  }

  function cartRequest(url, body, check) {
    if (check && Date.now() >= check.deadline) return Promise.reject(checkoutFailure('timeout', 'Checkout verification timed out'));
    var record;
    if (check && body) {
      if (check.editVersion !== cartEditVersion) return Promise.reject(checkoutFailure('cart_changed', 'Cart changed before repair'));
      try {
        var earlier = pendingWrite();
        if (!check.locked || check.recoveryStorageUnavailable ||
            (earlier && earlier.cart === check.cartFingerprint && earlier.settled !== true)) {
          throw checkoutFailure('recovery_unavailable', 'Safe cart repair is unavailable');
        }
        record = {cart:check.cartFingerprint, id:Date.now().toString(36) + '-' + Math.random().toString(36).slice(2),
          items:(body.items || []).map(function (item) { return {id:item.id, quantity:item.quantity}; }),
          codes:checkoutCodes(null, (check.requestedCodes || []).concat(check.recoveryCodes || []).join(','), check.removedCodes),
          removedCodes:check.removedCodes || []};
        window.localStorage.setItem(CHECKOUT_WRITE_KEY, JSON.stringify(record));
        if ((pendingWrite() || {}).id !== record.id) throw new Error('Recovery record was not saved');
        check.lastWriteId = record.id;
        if (body.items) body.items.forEach(function (item) {
          item.properties = Object.assign({}, item.properties, {_byob_repair:record.id});
        });
        else body.attributes = Object.assign({}, body.attributes, {byob_repair_write:record.id});
      } catch (_) {
        return Promise.reject(checkoutFailure('recovery_unavailable', 'Cart repair cannot be protected'));
      }
    }
    if (check && !body) {
      try { check.readWrite = pendingWrite(); } catch (_) { check.recoveryStorageUnavailable = true; }
    }
    var options = body ? {
      method: 'POST', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : {};
    // A timeout stops this attempt, not a received Shopify write. Late results
    // may clear their own record but cannot navigate or repeat an addition.
    var controller = check && !body ? new AbortController() : null;
    if (controller) options.signal = controller.signal;
    if (check) check.requests++;
    var request = Promise.resolve().then(function () { return fetch(url, options); }).then(function (response) {
      if (record) {
        var definitive = (response.ok && response.status !== 202) ||
          (response.status >= 400 && response.status < 500 && response.status !== 408);
        if (!definitive) throw checkoutFailure('write_unknown', 'Cart update may still be processing');
        settleWrite(record.id);
      }
      if (!response.ok) throw checkoutFailure('http_' + response.status, 'Cart request failed');
      return response.json();
    }).catch(function (error) {
      if (!error.checkoutReason) error.checkoutReason = 'network';
      throw error;
    });
    return checkoutWithin(request, check, controller).catch(function (error) {
      if (record) {
        try { var current = pendingWrite(); check.uncertainWrite = current && current.id === record.id && current.settled !== true; }
        catch (_) { check.uncertainWrite = true; }
        if (check.uncertainWrite) error.checkoutReason = 'write_unknown';
      }
      throw error;
    });
  }

  function hasBetterSelectedCoupon(cart, percent, requestedCodes) {
    var discounts = cart.discount_codes || [];
    if (!discounts.some(function (discount) {
      return String(discount.code || '').toUpperCase() === 'BUILDYOURSET' && discount.applicable === false;
    })) return false;
    var alternatives = {};
    discounts.forEach(function (discount) {
      var code = String(discount.code || '').toUpperCase();
      if (discount.applicable !== true || code === 'BUILDYOURSET' || code === 'BYOBFREEGIFTS' ||
          code.replace(/[^A-Z0-9]/g, '').indexOf('BUY2GET1FREE') !== -1) return;
      if (requestedCodes.some(function (requested) { return requested.toUpperCase() === code; })) alternatives[code] = true;
    });
    // Ajax line.discounts includes order-coupon allocations, which are excluded
    // from final_line_price and line_level_discount_allocations.
    var expected = 0;
    var savings = 0;
    var quantity = 0;
    var paidLines = cart.items.filter(isPaidByobCartLine);
    if (paidLines.some(function (item) {
      return (item.line_level_discount_allocations || []).some(function (allocation) {
        return String((allocation.discount_application || {}).title || '').toUpperCase() === 'BUILDYOURSET' && Number(allocation.amount) !== 0;
      });
    })) return false;
    paidLines.forEach(function (item) {
      expected += Number(item.original_line_price) * percent / 100;
      quantity += Number(item.quantity);
      savings += (item.discounts || []).reduce(function (sum, discount) {
        return sum + (alternatives[String(discount.title || '').toUpperCase()] ? Number(discount.amount) : 0);
      }, 0);
    });
    return Number.isFinite(expected) && expected > 0 && Number.isFinite(savings) && savings > 0 && savings + quantity / 2 >= expected;
  }

  function paidCartSignature(cart) {
    return JSON.stringify(cart.items.filter(function (item) {
      return (!item.properties || item.properties._byob_gift !== 'true') && !isLegacyEasyGiftBag(item);
    }).map(function (item) {
      var props = item.properties || {};
      return [item.variant_id || item.id || item.handle, item.quantity,
        Object.keys(props).sort().map(function (key) { return [key, props[key]]; })];
    }).sort(function (a, b) { return JSON.stringify(a).localeCompare(JSON.stringify(b)); }));
  }

  function hasRemovedCheckoutCode(cart, check) {
    return ((cart && cart.discount_codes) || []).some(function (discount) {
      return ((check && check.removedCodes) || []).some(function (code) {
        return String(discount.code || '').toUpperCase() === code.toUpperCase();
      });
    });
  }

  function repairAndVerifyByobOffer(cart, check) {
    if (!hasPaidByobItems(cart)) return Promise.resolve(cart);
    if (check) check.stage = 'offer_verification';
    var slots = {};
    cart.items.filter(isPaidByobCartLine).forEach(function (item) {
      var slot = item.properties && item.properties._byob_slot;
      if (slot == null) return; // Older marked carts predate numbered slots.
      if (!/^\d+$/.test(String(slot)) || slots[slot]) throw checkoutFailure('set_markers', 'BYOB bundle boundaries are ambiguous');
      slots[slot] = true;
    });
    var quantity = cart.items.filter(isPaidByobCartLine).reduce(function (sum, item) {
      return sum + Number(item.quantity);
    }, 0);
    if (quantity === 1 && !cart.items.some(function (item) {
      return isLegacyEasyGiftBag(item) || (item.properties && item.properties._byob_gift === 'true');
    })) {
      if (cart.items.some(function (item) {
        return (item.line_level_discount_allocations || []).some(function (allocation) {
          var title = String((allocation.discount_application || {}).title || '').toUpperCase();
          return Number(allocation.amount) > 0 && (title === 'BUILDYOURSET' || title === 'BYOBFREEGIFTS' ||
            title.replace(/[^A-Z0-9]/g, '').indexOf('BUY2GET1FREE') !== -1);
        });
      })) throw new Error('A below-tier set received an unearned promotion');
      if (cart.attributes && cart.attributes.byob_session === 'true' && !hasRemovedCheckoutCode(cart, check)) return Promise.resolve(cart);
      if (check) check.repaired = true;
      var belowTierSignature = paidCartSignature(cart);
      return cartRequest('/cart/update.js', {attributes:{byob_session:'true'}, discount:(check.requestedCodes || []).join(',')}, check).then(function (updated) {
        if (!updated || !Array.isArray(updated.items) || paidCartSignature(updated) !== belowTierSignature ||
            !updated.attributes || updated.attributes.byob_session !== 'true' || hasRemovedCheckoutCode(updated, check)) {
          throw checkoutFailure('cart_changed', 'Set status could not be restored');
        }
        return repairAndVerifyByobOffer(updated, check);
      });
    }
    var cartRoot = root();
    var config;
    try { config = JSON.parse(cartRoot.getAttribute('data-byob-offer') || '{}'); } catch (e) { config = {}; }
    if (!Array.isArray(config.tiers) || !config.tiers.length || !Number.isInteger(quantity) || quantity < 1) {
      throw checkoutFailure('offer_config', 'BYOB offer configuration is unavailable');
    }
    var percent = 0;
    var expectedGifts = {};
    config.tiers.forEach(function (tier) {
      if (!Number.isInteger(tier.quantity) || tier.quantity < 1 ||
          !Number.isFinite(tier.percent) || tier.percent < 0 || tier.percent > 100 || !Array.isArray(tier.gifts)) {
          throw checkoutFailure('offer_config', 'Invalid BYOB offer tier');
      }
      if (tier.quantity > quantity) return;
      percent = Math.max(percent, tier.percent);
      tier.gifts.forEach(function (gift) {
        if (!Number.isSafeInteger(gift.id) || gift.id < 1 || !gift.available) {
          var unavailable = checkoutFailure('gift_unavailable', 'An earned BYOB gift is unavailable');
          unavailable.customerMessage = 'One of your free gifts is currently unavailable. Your items are saved. Please review your set or try checkout again later.';
          throw unavailable;
        }
        expectedGifts[String(gift.id)] = true;
      });
    });
    var signature = paidCartSignature(cart);
    var updates = {};
    var found = {};
    cart.items.forEach(function (item) {
      if (isLegacyEasyGiftBag(item)) {
        if (!item.key) throw new Error('Legacy gift key is missing');
        updates[item.key] = 0;
        return;
      }
      if (!item.properties || item.properties._byob_gift !== 'true') return;
      if (!item.key) throw new Error('BYOB gift key is missing');
      var id = String(item.variant_id || item.id);
      var desired = expectedGifts[id] && !found[id] && item.properties._byob_item === 'gift' ? 1 : 0; // Replace older gift markers.
      if (desired) found[id] = true;
      if (Number(item.quantity) !== desired) updates[item.key] = desired;
    });
    var additions = Object.keys(expectedGifts).filter(function (id) { return !found[id]; }).map(function (id) {
      return { id: Number(id), quantity: 1, properties: { _byob_item: 'gift', _byob_gift: 'true' } };
    });
    var codes = (cartRoot.getAttribute('data-byob-discount-codes') || '').trim();
    if (!codes) throw new Error('BYOB checkout codes are unavailable');
    var preservedCodes = checkoutCodes(cart, codes, check && check.removedCodes);
    ((check && check.recoveryCodes) || []).forEach(function (code) {
      if (!preservedCodes.some(function (existing) { return existing.toUpperCase() === code.toUpperCase(); })) preservedCodes.push(code);
    });
    if (check) check.requestedCodes = preservedCodes;
    function verify(verified) {
      if (!verified || !Array.isArray(verified.items) || paidCartSignature(verified) !== signature ||
          !verified.attributes || verified.attributes.byob_session !== 'true' || hasPendingOps()) {
        throw checkoutFailure('cart_changed', 'BYOB cart changed during offer verification');
      }
      if (hasRemovedCheckoutCode(verified, check)) throw checkoutFailure('cart_changed', 'Coupon removal could not be verified');
      var gifts = {};
      var alternativeOfferVerified = hasBetterSelectedCoupon(verified, percent, preservedCodes);
      verified.items.forEach(function (item) {
        if (isLegacyEasyGiftBag(item)) throw new Error('Expired gift cleanup could not be verified');
        var allocations = item.line_level_discount_allocations || [];
        if (allocations.some(function (allocation) {
          return String((allocation.discount_application || {}).title || '').replace(/[^a-z0-9]/gi, '').toUpperCase().indexOf('BUY2GET1FREE') !== -1;
        })) throw new Error('An ended promotion is still applying');
        if (!isPaidByobCartLine(item) && (!item.properties || item.properties._byob_gift !== 'true') &&
            allocations.some(function (allocation) {
              return String((allocation.discount_application || {}).title || '').toUpperCase() === 'BUILDYOURSET' && Number(allocation.amount) > 0;
            })) throw new Error('BYOB discount reached an unrelated cart line');
        if ((!item.properties || item.properties._byob_gift !== 'true') && allocations.some(function (allocation) {
          return String((allocation.discount_application || {}).title || '').toUpperCase() === 'BYOBFREEGIFTS' && Number(allocation.amount) > 0;
        })) throw new Error('BYOB gift discount reached a paid cart line');
        if (item.properties && item.properties._byob_gift === 'true') {
          var id = String(item.variant_id || item.id);
          if (!expectedGifts[id] || gifts[id] || item.quantity !== 1 || item.final_line_price !== 0) {
            throw checkoutFailure('gift_price', 'BYOB gifts could not be verified as free');
          }
          gifts[id] = true;
        } else if (isPaidByobCartLine(item)) {
          var amount = allocations.reduce(function (sum, allocation) {
            return sum + (String((allocation.discount_application || {}).title || '').toUpperCase() === 'BUILDYOURSET' ? Number(allocation.amount) : 0);
          }, 0);
          var expected = Number(item.original_line_price) * percent / 100;
          // Discount Kit rounds per unit; each unit can differ by less than a cent.
          if (!Number.isFinite(expected) || !Number.isFinite(amount) ||
              (Math.abs(amount - expected) > Number(item.quantity) / 2 &&
                !(amount === 0 && alternativeOfferVerified))) {
            var error = checkoutFailure('product_discount', 'BYOB product discount could not be verified');
            error.customerMessage = 'We could not confirm your set discount. If you added another discount code, remove it from your cart and try again. Your items have been kept.';
            throw error;
          }
        }
      });
      if (Object.keys(gifts).length !== Object.keys(expectedGifts).length) throw checkoutFailure('gift_missing', 'An earned BYOB gift is missing');
      return verified;
    }
    if (cart.attributes && cart.attributes.byob_session === 'true' && !Object.keys(updates).length && !additions.length) {
      try { return Promise.resolve(verify(cart)); } catch (_) { /* Repair missing or stale allocations once. */ }
    }
    if (check) { check.repaired = true; check.stage = 'offer_repair'; }
    var update = { attributes: { byob_session: 'true' }, discount: preservedCodes.join(',') };
    if (Object.keys(updates).length) update.updates = updates;
    return cartRequest('/cart/update.js', update, check).then(function () {
      if (additions.length) return cartRequest('/cart/add.js', { items: additions }, check);
    }).then(function () { return cartRequest('/cart.js', null, check); }).then(function (verified) {
      if (check) check.stage = 'offer_verification';
      return verify(verified);
    });
  }

  function clearOrphanByobGiftsForCheckout(cart, check) {
    var visibleItems = cart.items.filter(function (item) { return !isHiddenCartLine(item); });
    if (!visibleItems.length || visibleItems.some(function (item) {
      return !item.properties || item.properties._byob_gift !== 'true';
    })) return Promise.resolve(cart);
    var updates = byobGiftLineUpdates(cart);
    if (!Object.keys(updates).length) throw new Error('BYOB gift lines could not be removed');
    return cartRequest('/cart/update.js', {
        updates: updates,
        attributes: { byob_session: '', byob_initiated_at: '' },
        sections: SECTION,
        sections_url: window.location.pathname
    }, check).then(function (updatedCart) {
      if (!updatedCart || !Array.isArray(updatedCart.items) ||
          updatedCart.items.some(function (item) {
            return item && item.properties && item.properties._byob_gift === 'true';
          }) || (updatedCart.attributes && String(updatedCart.attributes.byob_session || '') === 'true')) {
        throw new Error('BYOB gift cleanup could not be verified');
      }
      window.location.href = '/cart';
      return null;
    });
  }

  function checkoutErrorForButton(checkoutButton) {
    var directForm = checkoutButton && checkoutButton.closest('form[data-bbw-direct-checkout="true"]');
    if (directForm) return directForm.querySelector('[data-bbw-direct-checkout-error]');
    var cartCtas = checkoutButton && checkoutButton.closest('.cart__ctas');
    return cartCtas ? document.querySelector('.cart__ctas [data-cart-checkout-error]') || cartCtas.querySelector('[data-cart-checkout-error]') :
      document.querySelector('.bbw-cart [data-cart-checkout-error]');
  }

  function updateCheckoutButtons(busy) {
    if (!busy) {
      checkoutButtonStates.forEach(function (state, button) {
        button.disabled = state.disabled;
        button.removeAttribute('aria-busy');
        var restored = checkoutNeedsRecheck ? 'Check again' : state.text;
        if (state.label.textContent !== restored) state.label.textContent = restored;
      });
      checkoutButtonStates.clear();
      if (checkoutNeedsRecheck) {
        document.querySelectorAll('.bbw-cart__checkout, .cart__checkout-button').forEach(function (button) {
          var label = button.querySelector ? button.querySelector('.bbw-cart__checkout-label, .button-text') || button : button;
          if (!checkoutButtonLabels.has(button)) checkoutButtonLabels.set(button, label.textContent);
          if (label.textContent !== 'Check again') label.textContent = 'Check again';
        });
        if (checkoutRecoveryMessage) document.querySelectorAll('[data-cart-checkout-error]').forEach(function (notice) {
          if (notice.textContent !== checkoutRecoveryMessage) notice.textContent = checkoutRecoveryMessage;
          if (notice.hidden) notice.hidden = false;
        });
      }
      return;
    }
    document.querySelectorAll('.bbw-cart__checkout, .cart__checkout-button').forEach(function (button) {
      var label = button.querySelector ? button.querySelector('.bbw-cart__checkout-label, .button-text') || button : button;
      if (!checkoutButtonLabels.has(button)) checkoutButtonLabels.set(button, label.textContent);
      if (!checkoutButtonStates.has(button)) checkoutButtonStates.set(button, {disabled:button.disabled, label:label, text:checkoutButtonLabels.get(button)});
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label.textContent !== checkoutBusyMessage) label.textContent = checkoutBusyMessage;
    });
  }

  function syncCheckoutControls() {
    if (byobCheckoutPromise) updateCheckoutButtons(true);
    else if (checkoutNeedsRecheck) updateCheckoutButtons(false);
  }

  function watchCartPageCheckout() {
    if (checkoutControlsObserver || typeof MutationObserver !== 'function') return;
    var ctas = document.querySelector('.cart__ctas');
    if (!ctas) return;
    // Horizon can replace the summary after an asynchronous section request.
    // Observe only this cart section while preparing/rechecking checkout.
    checkoutControlsObserver = new MutationObserver(syncCheckoutControls);
    checkoutControlsObserver.observe(ctas.closest('[id^="shopify-section-"]') || ctas.parentElement,
      {childList:true, subtree:true, characterData:true, attributes:true, attributeFilter:['hidden']});
  }

  function checkoutDiagnostic(check, outcome, reason) {
    var detail = {version:1, outcome:outcome, reason:reason || '',
      duration_ms:Math.max(0, Date.now() - check.started), requests:check.requests,
      repaired:check.repaired, cart_type:check.cartType};
    // Local diagnostics remain available to QA. Remote events require consent
    // and use the existing Shopify publisher without delaying checkout.
    try { document.dispatchEvent(new CustomEvent('bbw:checkout-check', {detail:detail})); } catch (_) {}
    try {
      var shopify = window.Shopify || {};
      var privacy = shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function' ||
          privacy.analyticsProcessingAllowed() !== true) return;
      try { if (typeof window.gtag === 'function') window.gtag('event', 'based_checkout_check', detail); } catch (_) {}
      if (shopify.analytics && typeof shopify.analytics.publish === 'function') {
        Promise.resolve(shopify.analytics.publish('based:checkout_check', detail)).catch(function () {});
      }
    } catch (_) {}
  }

  function waitForCartUpdates(check) {
    return new Promise(function (resolve, reject) {
      function poll() {
        if (Date.now() >= check.deadline) return reject(checkoutFailure('timeout', 'Cart update timed out'));
        if (!hasPendingOps()) return resolve();
        setTimeout(poll, 50);
      }
      poll();
    });
  }

  function continueToCheckoutAfterByobCheck(href, checkoutButton, requiredVariantId, requiredQuantity) {
    if (byobCheckoutPromise) {
      if (requiredVariantId) return Promise.reject(checkoutFailure('checkout_busy', 'Another checkout is already preparing'));
      return byobCheckoutPromise;
    }
    var destination = href || '/checkout';
    var started = Date.now();
    var check = {started:started, deadline:started + 12000, requests:0, repaired:false, cartType:'unknown', stage:'cart_read'};
    var checkoutEditVersion;
    var error = checkoutErrorForButton(checkoutButton);
    if (error) error.hidden = true;
    checkoutBusyMessage = 'Preparing checkout…';
    watchCartPageCheckout();
    updateCheckoutButtons(true);
    checkoutDiagnostic(check, 'started');
    function prepare() {
      // BFCM BOGO (assets/bfcm-bogo-cart.js): settle the free gift lines
      // before the cart is read, so a last-second edit can't leave them stale.
      // Without BOGO on the page the read starts exactly as before.
      var bogo = window.BfcmBogoCart;
      var settleGifts = !!bogo && typeof bogo.settleForCheckout === 'function';
      function readCart() {
        checkoutEditVersion = cartEditVersion;
        check.editVersion = checkoutEditVersion;
        return cartRequest('/cart.js', null, check);
      }
      return waitForCartUpdates(check).then(function () {
        if (settleGifts) return bogo.settleForCheckout().catch(function (error) {
          if (error && error.code === 'timeout') throw checkoutFailure('timeout', 'BOGO gift update timed out');
        }).then(readCart);
        return readCart();
      }).then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) throw checkoutFailure('cart_response', 'Invalid cart response');
        var bogo = window.BfcmBogoCart;
        if (bogo && typeof bogo.checkoutProblem === 'function') {
          var bogoProblem = bogo.checkoutProblem(cart);
          if (bogoProblem === 'gift_missing') {
            var continueWithoutGifts = false;
            try {
              continueWithoutGifts = window.confirm('We could not add the free duffel to your cart. Continue to checkout with the items currently in your cart?');
            } catch (_) {}
            if (!continueWithoutGifts) {
              var missingError = checkoutFailure('bogo_gift_missing', 'BOGO gifts are missing');
              missingError.customerMessage = 'We could not add the free duffel. Your items are saved. Select checkout again to choose whether to continue without it.';
              throw missingError;
            }
            // Reading the choice can take time, especially with assistive
            // technology. Give cart verification its full window afterward.
            check.deadline = Date.now() + 12000;
            if (requiredVariantId) {
              // The launch purchase must still exist after the dialog. Other
              // tabs can change paid items, coupons, and offers while it waits.
              return waitForCartUpdates(check).then(readCart).then(function (freshCart) {
                if (!freshCart || !Array.isArray(freshCart.items)) throw checkoutFailure('cart_response', 'Invalid cart response');
                var freshProblem = bogo.checkoutProblem(freshCart);
                if (freshProblem && freshProblem !== 'gift_missing') {
                  var changedGiftError = checkoutFailure('bogo_gift', 'BOGO gifts changed while choosing checkout');
                  changedGiftError.customerMessage = 'Your cart changed while checkout was preparing. Please review your cart and try again.';
                  throw changedGiftError;
                }
                return freshCart;
              });
            }
          } else if (bogoProblem) {
            var giftError = checkoutFailure('bogo_gift', 'BOGO gift lines are not settled');
            giftError.customerMessage = 'We just updated your free gifts. Your items are saved. Please review your cart and try checkout again.';
            throw giftError;
          }
        }
        return cart;
      }).then(function (cart) {
        check.cartType = hasPaidByobItems(cart) ? 'byob' : 'ordinary';
        var cartRoot = root();
        checkPendingWrite(cart, check);
        check.requestedCodes = checkoutCodes(cart, cartRoot && cartRoot.getAttribute('data-byob-discount-codes'), check.removedCodes);
        return clearByobSessionIfMixedCart(cart, false, check).then(function (updatedCart) {
          if (cartNeedsByobSessionClear(updatedCart)) throw checkoutFailure('gift_cleanup', 'BYOB cleanup could not be verified');
          return updatedCart;
        });
      }).then(function (cart) { return clearOrphanByobGiftsForCheckout(cart, check); })
        .then(function (cart) { return cart ? repairAndVerifyByobOffer(cart, check) : null; })
        .then(function (cart) {
          if (!cart) { checkoutDiagnostic(check, 'blocked', 'gift_only_cart'); return false; }
          if (requiredVariantId && directCheckoutQuantity(cart, requiredVariantId) < (requiredQuantity || 1)) {
            throw checkoutFailure('purchase_missing', 'The selected item is no longer in the cart');
          }
          if (hasPendingOps() || cartEditVersion !== checkoutEditVersion) {
            throw checkoutFailure('cart_changed', 'Cart changed during checkout verification');
          }
          if (cartNeedsByobSessionClear(cart)) throw checkoutFailure('gift_cleanup', 'BYOB cleanup could not be verified');
          if (Date.now() >= check.deadline) throw checkoutFailure('timeout', 'Checkout verification timed out');
          var next = checkoutDestinationForCart(cart, destination, (check.requestedCodes || []).concat(check.recoveryCodes || []), check.removedCodes);
          if (check.lastWriteId) finishWrite(check.lastWriteId);
          checkoutDiagnostic(check, 'ready');
          window.location.href = next;
          return true;
        });
    }
    var locks = window.navigator && window.navigator.locks;
    var preparation = Promise.resolve().then(function () {
      if (!locks || typeof locks.request !== 'function') return prepare();
      return locks.request('bbw-checkout-repair', {mode:'exclusive', ifAvailable:true}, function (lock) {
        if (!lock) throw checkoutFailure('checkout_busy', 'Another tab is preparing checkout');
        check.locked = true;
        return prepare();
      });
    });
    byobCheckoutPromise = preparation.catch(function (err) {
        checkoutDiagnostic(check, 'blocked', err.checkoutReason || check.stage);
        error = checkoutErrorForButton(checkoutButton);
        if (error) {
          error.textContent = err.customerMessage || (err.checkoutReason === 'write_unknown' ?
            'Your cart update is still unconfirmed. Your items are saved. Select Check again to recheck safely. If it keeps happening, please contact us for help.' :
            err.checkoutReason === 'recovery_unavailable' ? 'We could not safely prepare your cart update. Your items are saved. Please review your cart or contact us for help.' :
            err.checkoutReason === 'checkout_busy' ? 'Another tab is preparing checkout. Please wait a moment and try again here.' :
            err.checkoutReason === 'timeout' || err.checkoutReason === 'network' ||
            String(err.checkoutReason || '').indexOf('http_') === 0 ?
            'Your connection is taking longer than expected. Your items are saved. Please try checkout again.' :
            err.checkoutReason === 'cart_changed' ? 'Your cart changed while checkout was preparing. Please try checkout again.' :
            'We could not prepare checkout. Your items are saved. Please try checkout again or review your cart.');
          error.hidden = false;
          if (check.uncertainWrite) checkoutRecoveryMessage = error.textContent;
        }
        return false;
      }).finally(function () {
        if (check.uncertainWrite) checkoutNeedsRecheck = true;
        updateCheckoutButtons(false);
        byobCheckoutPromise = null;
        if (!checkoutNeedsRecheck && checkoutControlsObserver) { checkoutControlsObserver.disconnect(); checkoutControlsObserver = null; }
      });
    return byobCheckoutPromise;
  }

  // Offer pages need proof that mixed-cart cleanup finished before checkout.
  window.bbwCartContinueToCheckout = function (href, requiredVariantId) {
    function requireOfferInCart(cart) {
      if (!requiredVariantId) return;
      var present = cart.items.some(function (item) {
        var variantId = Number(item && (item.variant_id || item.id));
        var quantity = !item || item.quantity === undefined ? 1 : Number(item.quantity);
        return variantId === Number(requiredVariantId) && quantity > 0;
      });
      if (!present) {
        var error = new Error('NCO offer is no longer in the cart');
        error.code = 'NCO_OFFER_MISSING';
        throw error;
      }
    }
    return fetch('/cart.js')
      .then(function (response) {
        if (!response.ok) throw new Error('Cart read failed');
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
        requireOfferInCart(cart);
        var needsClear = cartNeedsByobSessionClear(cart, true);
        var hasByobGift = cart.items.some(function (item) {
          return item && item.properties && item.properties._byob_gift === 'true';
        });
        return clearByobSessionIfMixedCart(cart, true).then(function () {
          if (!needsClear) return;
          return fetch('/cart.js')
            .then(function (response) {
              if (!response.ok) throw new Error('Cart verification failed');
              return response.json();
            })
            .then(function (verifiedCart) {
              if (!verifiedCart || !Array.isArray(verifiedCart.items) ||
                cartNeedsByobSessionClear(verifiedCart, true) ||
                (hasByobGift && verifiedCart.items.some(function (item) {
                  return item && item.properties && item.properties._byob_gift === 'true';
                }))) {
                throw new Error('BYOB cleanup could not be verified');
              }
              requireOfferInCart(verifiedCart);
            });
        });
      })
      .then(function () { window.location.href = href || '/checkout'; });
  };

  function hasPendingOps() {
    if (pendingCartAdds > 0) return true;
    for (var k in lines) if (lines[k] && (lines[k].inFlight || lines[k].sendTimer)) return true;
    return false;
  }

  // Tell the rest of the ecosystem (gift/discount apps, legacy listeners) that
  // the cart changed — the legacy cart did this too. Tagged so our own
  // cart:update listener can ignore it (no self-loop).
  function announce() {
    try {
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'bbw-cart' } }));
    } catch (e) { /* older browsers: skip */ }
  }

  /* ── Morph: reconcile server HTML into the live drawer ─────
     Item nodes are keyed on data-key and REUSED: unchanged rows
     aren't touched at all; changed rows update their body markup
     while media <img> is never recreated.
     Non-item regions (header, footer, empty state) are small and
     imageless, so they're replaced wholesale when they differ. */
  function patchItem(oldItem, newItem) {
    if (oldItem.outerHTML === newItem.outerHTML) return;
    var oldBody = oldItem.querySelector('.bbw-cart__item-body');
    var newBody = newItem.querySelector('.bbw-cart__item-body');
    if (oldBody && newBody) {
      if (oldBody.outerHTML !== newBody.outerHTML) oldBody.outerHTML = newBody.outerHTML;
      return;
    }

    var pairs = [
      ['.bbw-cart__qty-num', 'text'],
      ['.bbw-cart__item-price', 'html'],
      ['.bbw-cart__item-info', 'html'],
      ['.bbw-cart__gift-badge', 'presence']
    ];
    pairs.forEach(function (p) {
      var o = oldItem.querySelector(p[0]);
      var n = newItem.querySelector(p[0]);
      if (p[1] === 'presence') {
        if (o && !n) o.parentNode.removeChild(o);
        else if (!o && n) {
          var top = oldItem.querySelector('.bbw-cart__item-top');
          if (top) top.appendChild(n.cloneNode(true));
        }
        return;
      }
      if (!o || !n) return;
      if (p[1] === 'text') { if (o.textContent !== n.textContent) o.textContent = n.textContent; }
      else { if (o.innerHTML !== n.innerHTML) o.innerHTML = n.innerHTML; }
    });
  }

  function morph(html) {
    var cur = contentEl();
    if (!cur || !html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('[data-cart-content]');
    if (!next) return;
    var fw = parsed.querySelector('[data-bbw-wallets] shopify-accelerated-checkout-cart');
    if (fw) fetchedWalletComp = fw;

    var curList = cur.querySelector('.bbw-cart__items');
    var nextList = next.querySelector('.bbw-cart__items');

    // Structure changed (filled <-> empty): replace everything once.
    if (!curList || !nextList) {
      if (cur.innerHTML !== next.innerHTML) cur.innerHTML = next.innerHTML;
      syncWalletFlags(cur, next);
      // The replacement HTML carries the SERVER's default data-layout, so the
      // assigned variant has to be re-stamped here too. Without this an
      // empty-to-filled resync drops the shopper back to the control layout.
      applyUpsell();
      syncRailBars();
      return;
    }

    // 1) Items — keyed reconciliation.
    var oldByKey = {};
    curList.querySelectorAll('[data-cart-item]').forEach(function (el) {
      oldByKey[el.getAttribute('data-key')] = el;
    });
    var newItems = nextList.querySelectorAll('[data-cart-item]');
    var cursor = null; // last placed node, for ordering
    newItems.forEach(function (newItem) {
      var key = newItem.getAttribute('data-key');
      var node = oldByKey[key];
      if (node) {
        patchItem(node, newItem);
        node.classList.remove('is-removing');
        delete oldByKey[key];
      } else {
        node = newItem.cloneNode(true);
      }
      // Ensure order matches server order without re-inserting settled nodes.
      var expectedPrev = cursor;
      var actualPrev = node.previousElementSibling;
      if (node.parentNode !== curList || actualPrev !== expectedPrev) {
        if (expectedPrev && expectedPrev.nextSibling) curList.insertBefore(node, expectedPrev.nextSibling);
        else if (expectedPrev) curList.appendChild(node);
        else curList.insertBefore(node, curList.firstChild);
      }
      cursor = node;
    });
    // Remove rows the server no longer has.
    for (var k in oldByKey) {
      if (oldByKey[k].parentNode) oldByKey[k].parentNode.removeChild(oldByKey[k]);
    }

    // 2) Header + footer + anything else: swap only when different.
    ['.bbw-cart__header', '.bbw-cart__gift-progress', '.bbw-cart__offers-live', '.bbw-cart__footer'].forEach(function (sel) {
      var o = cur.querySelector(sel);
      var n = next.querySelector(sel);
      if (o && n && o.innerHTML !== n.innerHTML) o.innerHTML = n.innerHTML;
    });

    // 3) Reward bar: patch in place (state attr, text, fill width) so the CSS
    //    width transition animates instead of the node being recreated.
    var oRw = cur.querySelector('.bbw-cart__reward');
    var nRw = next.querySelector('.bbw-cart__reward');
    if (oRw && !nRw) {
      oRw.parentNode.removeChild(oRw);
    } else if (!oRw && nRw) {
      var hdr = cur.querySelector('.bbw-cart__header');
      if (hdr) hdr.insertAdjacentElement('afterend', nRw.cloneNode(true));
    } else if (oRw && nRw) {
      oRw.setAttribute('data-reward-state', nRw.getAttribute('data-reward-state') || '');
      var oTxt = oRw.querySelector('.bbw-cart__reward-text');
      var nTxt = nRw.querySelector('.bbw-cart__reward-text');
      if (oTxt && nTxt && oTxt.outerHTML !== nTxt.outerHTML) oTxt.outerHTML = nTxt.outerHTML;
      var oFill = oRw.querySelector('.bbw-cart__reward-fill');
      var nFill = nRw.querySelector('.bbw-cart__reward-fill');
      if (oFill && nFill && oFill.style.width !== nFill.style.width) oFill.style.width = nFill.style.width;
    }

    // 4) Upsell rail: swap contents when they differ (a just-added product
    //    drops out via the server-side dedupe); handle presence changes by
    //    anchoring to the items list.
    var oUp = cur.querySelector('.bbw-cart__upsells');
    var nUp = next.querySelector('.bbw-cart__upsells');
    if (oUp && !nUp) {
      oUp.parentNode.removeChild(oUp);
    } else if (!oUp && nUp) {
      var itemsEl = cur.querySelector('.bbw-cart__items');
      if (itemsEl) itemsEl.insertAdjacentElement('afterend', nUp.cloneNode(true));
    } else if (oUp && nUp && oUp.innerHTML !== nUp.innerHTML) {
      oUp.innerHTML = nUp.innerHTML;
    }
    syncWalletFlags(cur, next);
    // A fresh block arrives holding the server's data-layout, so re-stamp the
    // active variant before measuring the rail.
    applyUpsell();
    syncRailBars();
  }

  /* ── Express wallets: the container lives OUTSIDE [data-cart-content] so
     its Shopify-injected iframes are never destroyed by a morph. The fresh
     render's data-cart-* flags are copied onto the live content node, then
     visibility is just a [hidden] toggle — the wallet reads the live cart
     at tap time, so its content never needs re-rendering. */
  function syncWalletFlags(cur, next) {
    if (next) {
      cur.setAttribute('data-cart-items', next.getAttribute('data-cart-items') || '0');
      cur.setAttribute('data-cart-byob', next.getAttribute('data-cart-byob') || 'false');
      cur.setAttribute('data-cart-bfcm-gift', next.getAttribute('data-cart-bfcm-gift') || 'false');
    }
    syncWallets();
    if (byobCheckoutPromise) updateCheckoutButtons(true);
    else if (checkoutNeedsRecheck) updateCheckoutButtons(false);
  }
  /* Shopify's wallet script boots the component against the cart cookie as
     of page load — a page that loaded with an EMPTY cart has no cookie, the
     boot bails, and the dead instance may carry stamped state attributes.
     First time the cart gains items on such a page, swap in the PRISTINE
     component from the latest fetched section render (re-filtered through
     the inline allowlist hook) so it boots clean against the now-live cart.
     null = not yet measured on this page. */
  var walletsBornEmpty = null;
  var fetchedWalletComp = null;
  function reinitWalletComponent(wrap) {
    var comp = wrap.querySelector('shopify-accelerated-checkout-cart');
    if (!comp || !comp.parentNode) return;
    var fresh;
    if (fetchedWalletComp) {
      fresh = document.importNode(fetchedWalletComp, true);
      if (window.__bbwFilterWalletComp && !window.__bbwFilterWalletComp(fresh)) return;
    } else {
      fresh = document.createElement('shopify-accelerated-checkout-cart');
      for (var i = 0; i < comp.attributes.length; i++) {
        if (comp.attributes[i].name === 'disabled') continue;
        fresh.setAttribute(comp.attributes[i].name, comp.attributes[i].value);
      }
    }
    comp.parentNode.replaceChild(fresh, comp);
  }
  function syncWallets() {
    var r = root();
    if (!r) return;
    var content = contentEl();
    if (!content) return;
    var items = parseInt(content.getAttribute('data-cart-items') || '0', 10);
    var byob = content.getAttribute('data-cart-byob') === 'true';
    var bogo = r.getAttribute('data-bfcm-cart-enabled') === 'true' || content.getAttribute('data-cart-bfcm-gift') === 'true';
    if (walletsBornEmpty === null) walletsBornEmpty = items <= 0;
    var wallets = r.querySelector('[data-bbw-wallets]');
    if (wallets) {
      wallets.hidden = items <= 0 || byob || bogo;
      if (!wallets.hidden && walletsBornEmpty) {
        walletsBornEmpty = false;
        reinitWalletComponent(wallets);
      }
    }
    // Guarantee line (lives outside the morph zone with the wallets so it can
    // sit BELOW them): items-only gate — stays visible during BYOB sessions.
    var post = r.querySelector('[data-bbw-postfooter]');
    if (post) post.hidden = items <= 0;
  }

  var cartPageExpressSeq = 0;
  function syncCartPageExpressCheckout() {
    var wallets = Array.prototype.slice.call(document.querySelectorAll('[data-cart-express-checkout]'));
    if (!wallets.length) return Promise.resolve();
    var my = ++cartPageExpressSeq;
    wallets.forEach(function (wallet) { wallet.hidden = true; });
    return fetch('/cart.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Cart read failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (my !== cartPageExpressSeq || !cart || !Array.isArray(cart.items)) return;
        var byob = (cart.attributes && String(cart.attributes.byob_session || '') === 'true') ||
          cart.items.some(isByobCartLine);
        var bogo = (root() && root().getAttribute('data-bfcm-cart-enabled') === 'true') || cart.items.some(function (item) {
          return item && item.properties && item.properties._bfcm_gift === 'true';
        });
        wallets.forEach(function (wallet) { wallet.hidden = !cart.items.length || byob || bogo; });
      })
      .catch(function () {});
  }

  function applySections(sections) {
    if (sections && sections[SECTION]) morph(sections[SECTION]);
  }

  /* ── Re-sync: let server-side gift/discount apps settle ────
     Applies only if it is still the newest request AND no user
     edit is mid-flight (never stomp optimistic state). */
  function resyncOnce(checkEasyGiftBags) {
    if (hasPendingOps()) { scheduleResync(); return; }
    var my = ++seq;
    fetch('/?sections=' + SECTION)
      .then(function (r) { return r.json(); })
      .then(function (sections) { if (my === seq && !hasPendingOps()) applySections(sections); })
      .then(function () { return fetch('/cart.js'); })
      .then(function (r) { return r.json(); })
      .then(clearByobSessionIfMixedCart)
      .then(function (cart) {
        if (!checkEasyGiftBags || my !== seq || hasPendingOps()) return cart;
        return removeSurplusEasyGiftBags(cart, my);
      })
      .then(function (cart) { if (my === seq && cart) updateCount(cart.item_count); })
      .catch(function () {});
  }
  function scheduleResync() {
    resyncTimers.forEach(clearTimeout);
    resyncTimers = [650, 1400, 2600].map(function (delay, index) {
      return window.setTimeout(function () { resyncOnce(index === 2); }, delay);
    });
  }

  function suppressCartEditAtc(reason) {
    if (window.basedAtcAnalyticsGuard && typeof window.basedAtcAnalyticsGuard.suppressAddToCart === 'function') {
      window.basedAtcAnalyticsGuard.suppressAddToCart(reason, 4000);
    }
  }

  function dedupeCartAddAtc(reason) {
    if (window.basedAtcAnalyticsGuard && typeof window.basedAtcAnalyticsGuard.dedupeAddToCart === 'function') {
      window.basedAtcAnalyticsGuard.dedupeAddToCart(reason, 2500);
    }
  }

  function directCheckoutQuantity(cart, variantId) {
    return cart.items.reduce(function (quantity, item) {
      return quantity + (!isHiddenCartLine(item) && String(item.variant_id || item.id) === String(variantId) ? Number(item.quantity) || 0 : 0);
    }, 0);
  }

  function directCheckoutRoot() {
    var route = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof route === 'string' && /^\/(?:[^/?#]+\/)*$/.test(route) ? route : '/';
  }

  function directCheckoutRequest(url, options) {
    var check = { deadline: Date.now() + 12000 };
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    options = options || {};
    if (controller) options.signal = controller.signal;
    return checkoutWithin(Promise.resolve().then(function () { return fetch(url, options); }), check, controller)
      .then(function (response) {
        return checkoutWithin(Promise.resolve().then(function () { return response.json(); }), check, controller)
          .then(function (data) { return { response: response, data: data }; });
      });
  }

  function readDirectCheckoutCart(route) {
    return directCheckoutRequest(route + 'cart.js', { cache: 'no-store', headers: { 'Accept': 'application/json' } }).then(function (result) {
      if (!result.response.ok || !result.data || !Array.isArray(result.data.items)) throw new Error('Cart read failed');
      return result.data;
    });
  }

  // Only the launch form opts in. Keep the ordinary and exclusive-offer add
  // paths intact, and use the normal checked checkout so existing sets survive.
  function submitDirectCheckout(form, button) {
    var state = directCheckoutForms.get(form);
    if (!state) { state = { busy: false, attempt: null }; directCheckoutForms.set(form, state); }
    if (state.busy || (button && button.disabled)) return;
    var error = form.querySelector('[data-bbw-direct-checkout-error]');
    var review = form.querySelector('[data-bbw-direct-checkout-review]');
    function showError(message) {
      if (error) { error.textContent = message; error.hidden = false; }
      if (review) review.hidden = false;
    }
    if (byobCheckoutPromise) { showError('Checkout is already preparing. Please wait a moment and try again.'); return; }
    var data = new FormData(form);
    var id = String(data.get('id') || '');
    var quantity = Number(data.get('quantity') || 1);
    if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 1) {
      showError('Please select an available option and try again.'); return;
    }
    var route = directCheckoutRoot();
    var controls = Array.from(form.elements).filter(function (control) { return control.type !== 'hidden'; });
    var previous = controls.map(function (control) {
      var label = control.querySelector && control.querySelector('[data-bbw-pdp-cta-label], .bbw-fab__text');
      return { control: control, disabled: control.disabled, label: label, text: label && label.textContent };
    });
    state.busy = true;
    if (error) error.hidden = true;
    if (review) review.hidden = true;
    previous.forEach(function (saved) {
      saved.control.disabled = true;
      if (saved.control.type === 'submit') saved.control.setAttribute('aria-busy', 'true');
      if (saved.label) saved.label.textContent = 'PREPARING…';
    });
    form.setAttribute('aria-busy', 'true');
    function verifyAttempt(cart) {
      var actual = directCheckoutQuantity(cart, state.attempt.id);
      var before = state.attempt.expected - state.attempt.quantity;
      if (actual < state.attempt.expected) {
        if (state.attempt.rejection && actual === before) {
          var rejected = new Error('Cart rejected the selected option');
          rejected.customerMessage = state.attempt.rejection;
          state.attempt = null;
          throw rejected;
        }
        var unknown = new Error('The add could not be confirmed');
        unknown.customerMessage = actual > before ?
          'Only part of the requested quantity was added. Please review your cart before trying again. We will not add another item while this update is unconfirmed.' :
          'We could not confirm the cart update. Please review your cart before trying again. We will not add another item while this update is unconfirmed.';
        throw unknown;
      }
      return cart;
    }
    waitForCartUpdates({ deadline: Date.now() + 12000 }).then(function () { return readDirectCheckoutCart(route); })
      .then(function (cart) {
        if (state.attempt) {
          verifyAttempt(cart);
          if (state.attempt.id === id && state.attempt.quantity === quantity) return cart;
        }
        if (byobCheckoutPromise) throw checkoutFailure('checkout_busy', 'Checkout is already preparing');
        state.attempt = { id: id, quantity: quantity, expected: directCheckoutQuantity(cart, id) + quantity };
        cartEditVersion++;
        pendingCartAdds++;
        dedupeCartAddAtc('bbw-cart:direct-checkout');
        return directCheckoutRequest(route + 'cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: data })
          .then(function (result) {
            var status = Number(result.data && result.data.status) || result.response.status;
            if (status >= 400 && status < 500 && status !== 408) {
              state.attempt.rejection = result.data && typeof result.data.description === 'string' ? result.data.description : 'This option could not be added. Please check its availability and try again.';
            }
            // Even a 422 can add the stock that remains. Every response needs
            // readback before deciding whether any retry is safe.
          }).catch(function () {
            // A lost response cannot prove the add failed. Read the quantity.
          }).then(function () { return readDirectCheckoutCart(route); })
          .then(verifyAttempt)
          .then(function (verified) { updateCount(verified.item_count); announce(); return verified; })
          .finally(function () { pendingCartAdds--; });
      }).then(function () {
        // Release the add counter before the shared checker waits for edits.
        return continueToCheckoutAfterByobCheck(route + 'checkout', button, id, state.attempt.expected);
      }).then(function (ready) {
        // A purchase may have changed quantities without rendering the drawer.
        // On failure, load the full cart before the customer edits those rows.
        if (!ready) window.location.href = route + 'cart';
      }).catch(function (failure) {
        showError(failure.customerMessage || 'We could not prepare checkout. Please try again or review your cart. Any confirmed item is saved and will not be added twice.');
        if (state.attempt) window.location.href = route + 'cart';
      }).finally(function () {
        state.busy = false;
        form.removeAttribute('aria-busy');
        previous.forEach(function (saved) {
          saved.control.disabled = saved.disabled;
          saved.control.removeAttribute('aria-busy');
          if (saved.label) saved.label.textContent = saved.text;
        });
      });
  }

  /* ── Quantity changes: optimistic, serialized per line ───── */
  function itemEl(key) {
    var r = root();
    return r ? r.querySelector('[data-cart-item][data-key="' + key + '"]') : null;
  }

  function renderOptimistic(key) {
    var st = lines[key];
    var el = itemEl(key);
    if (!st || !el) return;
    var num = el.querySelector('.bbw-cart__qty-num');
    if (num) num.textContent = st.desired;
    el.classList.toggle('is-removing', st.desired === 0);
    el.classList.add('is-busy');
  }

  function sendLine(key) {
    var st = lines[key];
    if (!st || st.inFlight) return;
    st.inFlight = true;
    var sending = st.desired;
    var my = ++seq;
    suppressCartEditAtc('bbw-cart:quantity-change');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: sending,
        sections: SECTION,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        st.inFlight = false;
        if (st.desired !== sending) { sendLine(key); return; } // user kept clicking — send final value
        delete lines[key];
        var el = itemEl(key);
        if (el) el.classList.remove('is-busy');
        if (my === seq) {
          if (cart && cart.sections) applySections(cart.sections);
          if (cart) updateCount(cart.item_count);
        }
        announce();
        scheduleResync();
      })
      .catch(function () {
        st.inFlight = false;
        delete lines[key];
        resyncOnce(); // restore truth from server
      });
  }

  function queueChange(key, delta, absolute) {
    cartEditVersion++;
    var st = lines[key];
    if (!st) {
      var el = itemEl(key);
      var num = el ? el.querySelector('.bbw-cart__qty-num') : null;
      var current = num ? parseInt(num.textContent, 10) || 0 : 0;
      st = lines[key] = { desired: current, inFlight: false, sendTimer: null };
    }
    st.desired = absolute !== undefined ? absolute : Math.max(0, st.desired + delta);
    renderOptimistic(key);
    // Collapse rapid clicks into one request; removals send immediately.
    if (st.sendTimer) clearTimeout(st.sendTimer);
    var wait = st.desired === 0 ? 0 : 220;
    st.sendTimer = window.setTimeout(function () {
      st.sendTimer = null;
      sendLine(key);
    }, wait);
  }

  /* ── Add to cart: intercept the redesign ATC forms ─────────
     Capture phase + stopImmediatePropagation so we win over ANY
     theme handler that might let the form post natively. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-bbw-add]');
    if (!form) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    var btn = form.querySelector('button[type="submit"]');
    if (form.dataset && form.dataset.bbwDirectCheckout === 'true') {
      submitDirectCheckout(form, btn);
      return;
    }
    if (btn && btn.disabled) return;
    var label = btn ? btn.querySelector('.bbw-atc__label') : null;
    var prevLabel = label ? label.textContent : '';
    var fd = new FormData(form);
    fd.append('sections', SECTION);
    fd.append('sections_url', window.location.pathname);
    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
    if (label) label.textContent = 'ADDING…';

    /* Snapshot the layout the shopper actually SAW, at click time, from the
       block this form sits in. Reading the global after the request resolves
       reports null whenever the upsell runs without the A/B (nothing sets the
       document attribute then), and misattributes the conversion if the cell
       changes while the add is in flight. Validated through upsellLayout so a
       hand-edited attribute cannot reach analytics. */
    var upsellBlock = form.closest ? form.closest('.bbw-cart__upsells') : null;
    var addedFromLayout = upsellBlock ? upsellLayout(upsellBlock.getAttribute('data-layout')) : null;

    var my = ++seq;
    cartEditVersion++;
    dedupeCartAddAtc('bbw-cart:add');
    pendingCartAdds++;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: fd
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || res.data.status) return; // e.g. sold out / over stock
        /* Conversion signal for the upsell A/B test: fires only for the cart
           upsell forms and carries the layout the shopper was shown. */
        if (form.hasAttribute('data-upsell-add')) {
          try {
            document.dispatchEvent(new CustomEvent('bbw:cart-upsell-add', {
              detail: {
                productId: form.getAttribute('data-product-id'),
                variantId: form.getAttribute('data-variant-id'),
                layout: addedFromLayout
              }
            }));
          } catch (e) {}
        }
        if (my === seq && res.data.sections) applySections(res.data.sections);
        open();
        announce();
        scheduleResync();
        return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          return clearByobSessionIfMixedCart(cart);
        }).then(function (cart) {
          if (my === seq && cart) updateCount(cart.item_count);
        });
      })
      .catch(function () {})
      .finally(function () {
        pendingCartAdds--;
        if (label && prevLabel) label.textContent = prevLabel;
        if (btn) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }
      });
  }, true);

  /* ── Delegated clicks (open / close / qty / remove) ──────── */
  document.addEventListener('click', function (e) {
    var checkout = e.target.closest && e.target.closest('.bbw-cart__checkout, .cart__checkout-button, button[name="checkout"]');
    if (checkout && !checkout.closest('[data-byob-checkout]')) {
      e.preventDefault();
      continueToCheckoutAfterByobCheck('/checkout', checkout);
      return;
    }

    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      open();
      return;
    }

    var r = root();
    if (!r || !r.contains(e.target)) return;

    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); close(); return; }

    var inc = e.target.closest('[data-qty-inc]');
    var dec = e.target.closest('[data-qty-dec]');
    var rem = e.target.closest('[data-cart-remove]');
    if (!inc && !dec && !rem) return;

    e.preventDefault();
    var trigger = inc || dec || rem;
    var key = trigger.getAttribute('data-key');

    if (rem) queueChange(key, 0, 0);
    else if (inc) queueChange(key, 1);
    else if (dec) queueChange(key, -1);
  });

  /* ── Legacy pages: react to the theme's cart:update event ──
     Legacy ATC flows (Horizon product-form, quick-add, sticky
     CTA…) dispatch document-level 'cart:update' after adding.
     We refresh + open; sequencing prevents double-render races
     with our own add path. */
  var refreshing = false;
  var openAfterRefresh = false;
  function refreshAndOpen(shouldOpen) {
    // A page that writes the cart itself (the BFCM BOGO picker) sends
    // open:false on its cart:update so the drawer refreshes without opening.
    if (shouldOpen !== false) openAfterRefresh = true;
    if (refreshing) return;
    refreshing = true;
    var my = ++seq;
    fetch('/?sections=' + SECTION)
      .then(function (r) { return r.json(); })
      .then(function (sections) { if (my === seq) applySections(sections); })
      .catch(function () {})
      .finally(function () {
        if (openAfterRefresh) open();
        openAfterRefresh = false;
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(clearByobSessionIfMixedCart)
          .then(function (cart) { if (cart) updateCount(cart.item_count); })
          .catch(function () {})
          .finally(function () { refreshing = false; openAfterRefresh = false; });
      });
  }
  document.addEventListener('cart:update', function (e) {
    if (e.detail && e.detail.source === 'bbw-cart') return; // our own announcement
    cartEditVersion++;
    refreshAndOpen(!(e.detail && e.detail.open === false));
    scheduleResync();
  });

  /* ── Escape closes ────────────────────────────────────────── */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });

  /* ── Upsell A/B activation (external test tool) ─────────────
     With the "A/B hold" theme setting on, the section renders the
     upsell block hidden (data-upsell-hold="1") and this hook is
     what reveals it, so a tool like Coframe/Jarvis owns who sees
     it and in which layout:

       window.BBWCartUpsell.enable('vertical' | 'horizontal')
       window.BBWCartUpsell.disable()
       window.BBWCartUpsell.state()   -> 'rows' | 'rail' | null

     A single attribute on <html> carries the choice, which is what
     makes a variant survive drawer morphs, re-opens and section
     re-renders — re-applied below wherever the drawer is rebuilt.
     A tool that injects BEFORE this file loads can set
     window.__bbwCartUpsellAB = 'vertical' instead; it is read at
     boot. The choice is kept in sessionStorage so a visitor holds
     their variant across page views even if one page misses the
     injection, which keeps the cells clean. */
  var UPSELL_ALIASES = {
    vertical: 'rows', rows: 'rows', list: 'rows', stacked: 'rows',
    horizontal: 'rail', rail: 'rail', cards: 'rail', carousel: 'rail'
  };
  var UPSELL_STORE_KEY = 'bbw:cart-upsell-ab';

  function upsellLayout(value) {
    var key = String(value == null ? '' : value).trim().toLowerCase();
    /* Own-property lookup only. A plain object inherits `__proto__`,
       `constructor` and friends, so an injected value matching one of those
       would resolve to an object instead of falling through to null, and that
       object would then be stringified into the layout attribute, session
       storage and the analytics payload. */
    return Object.prototype.hasOwnProperty.call(UPSELL_ALIASES, key) ? UPSELL_ALIASES[key] : null;
  }
  function rememberUpsell(layout) {
    try {
      if (layout) window.sessionStorage.setItem(UPSELL_STORE_KEY, layout);
      else window.sessionStorage.removeItem(UPSELL_STORE_KEY);
    } catch (e) {}
  }
  function rememberedUpsell() {
    try { return window.sessionStorage.getItem(UPSELL_STORE_KEY); } catch (e) { return null; }
  }
  /* Stamps the active layout onto every held block. data-layout is the same
     attribute the theme setting writes, so a variant renders through the
     production rules rather than a test-only style. */
  function applyUpsell() {
    var layout = upsellLayout(document.documentElement.getAttribute('data-bbw-upsell'));
    if (!layout) return;
    var held = document.querySelectorAll('.bbw-cart__upsells[data-upsell-hold="1"]');
    for (var i = 0; i < held.length; i++) held[i].setAttribute('data-layout', layout);
    syncRailBars();
  }
  function setUpsell(value, deactivate) {
    var previous = upsellLayout(document.documentElement.getAttribute('data-bbw-upsell'));
    var layout = upsellLayout(value);
    /* Invalid external values are rejected without changing an existing
       assignment. Only the public disable() path may deliberately clear a
       shopper's cell, persistence and DOM state. */
    if (!layout && !deactivate) return null;
    if (layout) document.documentElement.setAttribute('data-bbw-upsell', layout);
    else document.documentElement.removeAttribute('data-bbw-upsell');
    rememberUpsell(layout);
    applyUpsell();
    /* Exposure fires ONLY on a real transition into a cell. An unassigned boot
       (layout null), a repeat call naming the cell the shopper is already in,
       and disable() all stay silent — otherwise control traffic and ordinary
       re-renders would be recorded as exposures and the test would be reading
       its own noise. Deactivation gets a distinct event so it can still be
       tracked without being mistaken for exposure. */
    if (layout && layout !== previous) {
      try {
        document.dispatchEvent(new CustomEvent('bbw:cart-upsell', { detail: { layout: layout } }));
      } catch (e) {}
    } else if (!layout && previous) {
      try {
        document.dispatchEvent(new CustomEvent('bbw:cart-upsell-off', { detail: { previous: previous } }));
      } catch (e) {}
    }
    return layout;
  }
  window.BBWCartUpsell = {
    enable: function (value) { return setUpsell(value == null ? 'vertical' : value); },
    disable: function () { return setUpsell(null, true); },
    state: function () { return upsellLayout(document.documentElement.getAttribute('data-bbw-upsell')); },
    apply: applyUpsell
  };

  /* ── Upsell rail (data-layout="rail"): desktop scrolling ────
     The strip is mouse-draggable on desktop; a drag swallows the
     click it would otherwise fire, so ADD/links only trigger on
     a true click. The bottom stroke doubles as a gold scroll
     progress bar, synced here. Delegated — survives morphs. */
  function railList(target) {
    var list = target && target.closest ? target.closest('.bbw-cart__upsells-list') : null;
    if (!list) return null;
    var wrap = list.closest('.bbw-cart__upsells');
    return wrap && wrap.getAttribute('data-layout') === 'rail' ? list : null;
  }

  function syncRailBar(list) {
    var wrap = list.closest('.bbw-cart__upsells');
    var thumb = wrap ? wrap.querySelector('[data-rail-thumb]') : null;
    if (!thumb) return;
    var sw = list.scrollWidth;
    var cw = list.clientWidth;
    if (sw <= cw) {
      thumb.style.width = '100%';
      thumb.style.left = '0';
      return;
    }
    thumb.style.width = (cw / sw * 100) + '%';
    thumb.style.left = (list.scrollLeft / sw * 100) + '%';
  }
  function syncRailBars() {
    var r = root();
    if (!r) return;
    r.querySelectorAll('.bbw-cart__upsells[data-layout="rail"] .bbw-cart__upsells-list').forEach(syncRailBar);
  }
  // scroll doesn't bubble — capture catches it from any rail list.
  document.addEventListener('scroll', function (e) {
    var list = railList(e.target);
    if (list) syncRailBar(list);
  }, true);

  var railDrag = null;
  document.addEventListener('dragstart', function (e) {
    if (railList(e.target)) e.preventDefault(); // no native image/link ghosts
  });
  document.addEventListener('mousedown', function (e) {
    railDrag = null; // clear any stale drag (mouseup can land outside the window)
    var list = railList(e.target);
    if (!list || e.button !== 0) return;
    railDrag = { list: list, startX: e.clientX, startLeft: list.scrollLeft, moved: false };
  });
  document.addEventListener('mousemove', function (e) {
    if (!railDrag) return;
    if (!(e.buttons & 1)) {
      // Button was released outside the window — abort, never swallow a click.
      railDrag.list.classList.remove('is-dragging');
      railDrag = null;
      return;
    }
    var dx = e.clientX - railDrag.startX;
    if (!railDrag.moved && Math.abs(dx) > 4) railDrag.moved = true;
    if (railDrag.moved) {
      railDrag.list.scrollLeft = railDrag.startLeft - dx;
      railDrag.list.classList.add('is-dragging');
      e.preventDefault();
    }
  });
  document.addEventListener('mouseup', function () {
    if (!railDrag) return;
    var was = railDrag;
    railDrag = null;
    was.list.classList.remove('is-dragging');
    if (was.moved) {
      var swallow = function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
      };
      document.addEventListener('click', swallow, true);
      setTimeout(function () {
        document.removeEventListener('click', swallow, true);
      }, 0);
    }
  });

  /* Back/forward-cache restores resurrect the pre-navigation DOM, so
     data-cart-items / data-cart-byob can be stale against the live cart —
     a BYOB session started since would show wallets that bypass its
     checkout-URL discounts. Restore truth from the server. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) resyncOnce();
    syncCartPageExpressCheckout();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') syncCartPageExpressCheckout();
  });
  document.addEventListener('cart:update', function () {
    // Horizon replaces the cart section just after dispatching this event.
    window.setTimeout(function () { syncCartPageExpressCheckout(); syncCheckoutControls(); }, 0);
  });

  document.addEventListener('discount:update', function (event) {
    cartEditVersion++;
    var cart = event.detail && event.detail.resource;
    try {
      var record = pendingWrite();
      if (record && cart && Array.isArray(cart.discount_codes) && record.cart === cartFingerprint(cart)) {
        var codes = checkoutCodes(cart, '');
        record.removedCodes = (record.removedCodes || []).concat(record.codes.filter(function (code) {
          return !/^(BUILDYOURSET|BYOBFREEGIFTS)$/i.test(code) && !codes.some(function (current) { return current.toUpperCase() === code.toUpperCase(); });
        })).filter(function (code) { return !codes.some(function (current) { return current.toUpperCase() === code.toUpperCase(); }); });
        record.codes = codes;
        window.localStorage.setItem(CHECKOUT_WRITE_KEY, JSON.stringify(record));
      }
    } catch (_) {}
    window.setTimeout(function () { syncCartPageExpressCheckout(); syncCheckoutControls(); }, 0);
  });
  window.addEventListener('storage', function (event) {
    if (event.key === CHECKOUT_WRITE_KEY) cartEditVersion++;
  });

  syncRailBars();
  syncWallets();
  syncCartPageExpressCheckout();
  /* A valid explicit global (a tool that injected before this file) beats the
     remembered session value. Validate it before applying precedence so a
     malformed injection cannot suppress a shopper's valid saved cell. */
  var injectedUpsell = upsellLayout(window.__bbwCartUpsellAB);
  var savedUpsell = upsellLayout(rememberedUpsell());
  setUpsell(injectedUpsell || savedUpsell);
})();
